/**
 * g18: pick the model and effort for one run. The planner's choice wins over project hints, which
 * win over the task-type fallback; user picks and resumed sessions are kept exactly.
 */
import { classifyTaskType, escalateTier, MODEL_TIERS, profileFor, tierOfModel, } from '@viko/protocol';
const FALLBACK = {
    docs: 'fast',
    testing: 'fast',
    building: 'standard',
    bugfix: 'standard',
    refactor: 'standard',
    'image-generation': 'standard',
    'gpu-windows': 'standard',
    review: 'deep',
    research: 'deep',
    'goal-loop': 'deep',
};
const rank = (t) => MODEL_TIERS.indexOf(t);
/**
 * The tier a model string stands for on this agent; undefined when it belongs to the other family.
 * An id no profile knows is kept only when `trustUnknown` (user-authored project config); an
 * agent-supplied unknown id is dropped so it can't bypass the entitlement clamp.
 */
function tierFor(agent, model, tier, trustUnknown = false) {
    if (model) {
        const t = tierOfModel(agent, model);
        if (t)
            return { tier: t, model };
        if (agent === 'codex' && tierOfModel('claude', model))
            return tier ? { tier } : undefined;
        if (agent === 'claude' && tierOfModel('codex', model))
            return tier ? { tier } : undefined;
        if (!trustUnknown)
            return tier ? { tier } : undefined;
        return { tier, model }; // unknown id from user-authored config: pass through
    }
    return tier ? { tier } : undefined;
}
function tierModel(i, tier) {
    const p = profileFor(i.agent, tier);
    const o = i.execution?.models?.find((m) => m.agent === i.agent && m.tier === tier);
    return { model: o?.model ?? p.model, effort: o?.effort ?? p.effort, label: p.label };
}
export function resolveRunModel(i) {
    const { task } = i;
    if (task.userModel || task.userEffort) {
        return {
            model: task.userModel,
            effort: task.userEffort,
            tier: task.userModel ? tierOfModel(i.agent, task.userModel) : undefined,
            source: 'user',
            reason: 'chosen by you',
        };
    }
    if (i.resume?.model || i.resume?.effort) {
        return {
            model: i.resume.model,
            effort: i.resume.effort,
            tier: i.resume.model ? tierOfModel(i.agent, i.resume.model) : undefined,
            source: 'resume',
            reason: 'resumed session keeps its model',
        };
    }
    let source;
    let tier;
    let explicit;
    let why;
    const planner = tierFor(i.agent, task.model, task.tier);
    const hint = i.hints
        ?.filter((h) => (!h.agent || h.agent === i.agent) && h.labels.every((l) => task.labels.includes(l)))
        .map((h) => ({ h, r: tierFor(i.agent, h.model, h.tier, true) }))
        .find((x) => x.r?.tier);
    if (planner?.tier) {
        source = 'planner';
        tier = planner.tier;
        explicit = planner.model;
        why = task.modelReason ?? 'chosen by the planner';
    }
    else if (hint?.r?.tier) {
        source = 'hint';
        tier = hint.r.tier;
        explicit = hint.r.model;
        why = `label \`${hint.h.labels.join('`, `')}\` matches a project hint`;
    }
    else {
        const type = classifyTaskType(task.title, task.labels);
        const fb = FALLBACK[type];
        source = fb ? 'fallback' : 'default';
        tier = fb ?? i.projectDefaultTier ?? 'standard';
        explicit = undefined;
        why = fb ? `${type} work defaults to ${tier}` : `no signal; project default ${tier}`;
    }
    let reasonTail = '';
    const fails = i.failures ?? 0;
    if (i.escalation && fails > 0) {
        const { fast, standard } = i.escalation;
        let t = tier;
        if (t === 'fast' && fails >= fast)
            t = escalateTier(t);
        if (t === 'standard' && fails >= fast + standard)
            t = escalateTier(t);
        if (t !== tier) {
            tier = t;
            explicit = undefined;
            reasonTail += `; escalated after ${fails} failures`;
        }
    }
    const allowed = i.allowedTiers ?? MODEL_TIERS;
    // An unprofiled id can't be checked against the entitlement: use the tier's own model instead.
    if (explicit && i.allowedTiers && !tierOfModel(i.agent, explicit))
        explicit = undefined;
    if (allowed.length && !allowed.includes(tier)) {
        const lower = [...allowed]
            .filter((t) => rank(t) <= rank(tier))
            .sort((a, b) => rank(b) - rank(a))[0];
        const to = lower ?? [...allowed].sort((a, b) => rank(a) - rank(b))[0];
        reasonTail += `; ${tier} isn't available on this account, using ${to}`;
        tier = to;
        explicit = undefined;
    }
    const m = tierModel(i, tier);
    return {
        model: explicit ?? m.model,
        effort: m.effort,
        tier,
        source,
        reason: `${m.label}: ${why}${reasonTail}`,
    };
}
//# sourceMappingURL=model-choice.js.map